import React from "react";
import { Table } from "react-bootstrap";
import Loading from "../../components/loading";
import InterviewService, { InterviewAnalyticsFilters } from "../../services/interview.service";
import { AuthenticationService } from "../../services/auth.service";
import { AlertService } from "../../services/alert.service";
import { LOGIN_ROUTE } from "../../providers/routes";
import { t } from "../../translations/translation.service";
import { isValidLinkedIn, normalizeLinkedIn } from "../../util/validators";
import { buildInterviewTypeSummary, InterviewTypeSummary } from "./interviewType";
import { Dropdown, Field, Item, Menu, Multiselect } from "@zendeskgarden/react-dropdowns";
import Downshift from "downshift";
import { Tag } from "@zendeskgarden/react-tags";
// @ts-ignore: Side-effect CSS import is handled by the bundler
import "./index.scss";
import { ReactComponent as ClipboardIcon } from "@zendeskgarden/svg-icons/src/16/clipboard-list-stroke.svg";
import { ReactComponent as TrendingIcon } from "@zendeskgarden/svg-icons/src/16/arrow-trending-fill.svg";
import { ReactComponent as AlertIcon } from "@zendeskgarden/svg-icons/src/16/alert-error-fill.svg";
import { ReactComponent as CheckIcon } from "@zendeskgarden/svg-icons/src/16/check-lg-stroke.svg";

type InterviewReportState = {
    loading: boolean;
    exportLoading: boolean;
    currentPage: number;
    itemsPerPage: number;
    filters: InterviewReportFilters;
    months: OptionLike[];
    recruiters: SelectOption[];
    evaluators: SelectOption[];
    companies: SelectOption[];
    dashboardRows: DashboardRow[];
    detailsRows: GenericRow[];
    dataAnimationKey: number;
    interviewType: {
        client: number;
        proactive: number;
    };
    totalProcesses: number;
};

type SelectOption = {
    value: string;
    label: string;
};

type InterviewReportFilters = {
    month: string[];
    week: string;
    recruiter: string[];
    evaluator: string[];
    company: string[];
};

type InterviewFilterName = "month" | "recruiter" | "evaluator" | "company";

type InterviewFilterMode = "include" | "exclude";

type InterviewMultiSelectProps = {
    id: string;
    options: SelectOption[];
    selectedValues: string[];
    allLabel: string;
    mode?: InterviewFilterMode;
    onChange: (values: string[]) => void;
};

type GenericRow = Record<string, unknown>;

type OptionLike = string | number | Record<string, unknown>;

type DashboardMetrics<TValue> = {
    week1: TValue;
    week2: TValue;
    week3: TValue;
    week4: TValue;
    week5: TValue;
    totalMonth: TValue;
};

type DashboardRow = Partial<DashboardMetrics<unknown>> & {
    _id?: string | number;
    recruiterName?: unknown;
    evaluatorName?: unknown;
};

type DashboardAggregateRow = DashboardMetrics<number> & {
    evaluatorName: string;
};

type DetailsExtractedRow = {
    company: unknown;
    vacancy: unknown;
    candidate: unknown;
    linkedin: unknown;
    recruiter: unknown;
    stage: unknown;
    interviewDate: unknown;
    evaluator: unknown;
    registerDate: unknown;
};

const SELECT_ALL_VALUE = "__select_all__";
const DESELECT_ALL_VALUE = "__deselect_all__";

function InterviewMultiSelect({
    id,
    options,
    selectedValues,
    allLabel,
    mode = "include",
    onChange,
}: Readonly<InterviewMultiSelectProps>) {
    const optionLabels = new Map(options.map((option) => [option.value, option.label]));
    const optionValues = new Set(options.map((option) => option.value));
    const allOptionValues = options.map((option) => option.value);
    const isExclude = mode === "exclude";
    // In "exclude" mode an empty selection means every option is checked.
    const allChecked = selectedValues.length === 0;

    // The collapsed field shows the values that WILL appear in the report: in
    // "exclude" mode that is every option minus the unchecked (removed) ones.
    // While every option is still checked we keep the field empty so it shows
    // the "Todos selecionados" placeholder instead of listing every name.
    const keptValues = isExclude
        ? allOptionValues.filter((value) => !selectedValues.includes(value))
        : selectedValues;
    const displayedValues = isExclude && allChecked ? [] : keptValues;

    // In "exclude" mode the placeholder doubles as the "everything checked"
    // summary; when there are exclusions the kept names are rendered as chips.
    const placeholder = isExclude && allChecked ? t("Todos selecionados") : allLabel;

    const handleSelect = (values: string[] | null) => {
        const selectedItems = values || [];
        if (selectedItems.includes(SELECT_ALL_VALUE)) {
            // "Selecionar todos" clears every exclusion (all options checked).
            onChange([]);
            return;
        }
        if (selectedItems.includes(DESELECT_ALL_VALUE)) {
            // "Desmarcar todos" excludes every option.
            onChange(allOptionValues);
            return;
        }
        const picked = selectedItems.filter((value) => optionValues.has(value));
        if (isExclude) {
            if (allChecked) {
                // The field showed no chips ("Todos selecionados"): the clicked
                // item is the one to exclude.
                onChange(picked);
                return;
            }
            // Downshift toggles the CHECKED (kept) items; store the complementary
            // set as the excluded values.
            const pickedValues = new Set(picked);
            onChange(allOptionValues.filter((value) => !pickedValues.has(value)));
            return;
        }
        onChange(picked);
    };

    const renderOption = (label: string, checked: boolean) => (
        <span className="interview-filter-option">
            <span
                className={`interview-filter-checkbox${checked ? " is-checked" : ""}`}
                aria-hidden="true"
            >
                {checked && <CheckIcon className="interview-filter-check-icon" focusable="false" />}
            </span>
            <span className="interview-filter-option-label">{label}</span>
        </span>
    );

    // In "exclude" mode selectedValues holds the UNCHECKED (removed) items, so
    // an empty set means every item is checked. In "include" mode an empty set
    // also means "all", but a checked item is one present in selectedValues.
    const isOptionChecked = (value: string) =>
        isExclude ? !selectedValues.includes(value) : selectedValues.includes(value);

    return (
        <Dropdown
            selectedItems={displayedValues}
            onSelect={handleSelect}
            downshiftProps={{
                itemToString: (item: unknown) => (typeof item === "string" ? item : ""),
                stateReducer: (state, changes) => {
                    if (
                        changes.type === Downshift.stateChangeTypes.clickItem ||
                        changes.type === Downshift.stateChangeTypes.keyDownEnter
                    ) {
                        return { ...changes, isOpen: true, highlightedIndex: state.highlightedIndex };
                    }
                    return changes;
                },
            }}
        >
            <Field>
                <Multiselect
                    id={id}
                    className="interview-filter-multiselect"
                    placeholder={placeholder}
                    maxItems={1}
                    renderShowMore={(count) => `+${count}`}
                    renderItem={({ value, removeValue }) => (
                        <Tag size="small">
                            {optionLabels.get(String(value)) || String(value)}
                            <Tag.Close aria-label={t("Limpar")} onClick={removeValue} />
                        </Tag>
                    )}
                />
            </Field>
            <Menu className="interview-filter-menu" maxHeight="240px" zIndex={1200}>
                {isExclude && (
                    <Item value={SELECT_ALL_VALUE}>
                        <span className="interview-filter-option interview-filter-toggle-all">
                            {t("Selecionar todos")}
                        </span>
                    </Item>
                )}
                {isExclude && (
                    <Item value={DESELECT_ALL_VALUE}>
                        <span className="interview-filter-option interview-filter-toggle-all">
                            {t("Desmarcar todos")}
                        </span>
                    </Item>
                )}
                {options.map((option) => (
                    <Item key={option.value} value={option.value}>
                        {renderOption(option.label, isOptionChecked(option.value))}
                    </Item>
                ))}
            </Menu>
        </Dropdown>
    );
}

class InterviewReport extends React.Component<Record<string, never>, InterviewReportState> {
    latestRequestId = 0;
    unauthorizedHandled = false;
    readonly noInfoLabel = "Sem informação";
    readonly companyDirectKeys = ["Empresa", "companyName", "company", "empresa", "nomeEmpresa", "company_name", "cliente", "client"];
    readonly companyTokenKeys = ["empresa", "company", "cliente"];

    isMissingDisplayValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            return trimmed === "" || trimmed === "-" || trimmed === "--" || trimmed === "—";
        }

        return false;
    }

    formatDisplayValue(value: unknown): string {
        if (this.isMissingDisplayValue(value)) {
            return this.noInfoLabel;
        }

        return String(value);
    }

    getWeeklyTotals(rows: DashboardRow[]) {
        const weekKeys = ["week1", "week2", "week3", "week4", "week5"] as const;
        return weekKeys.map((key) => ({
            key,
            total: rows.reduce((sum, row) => sum + (Number(row?.[key]) || 0), 0),
        }));
    }

    renderMissingInfoWithAlert(value: unknown) {
        if (!this.isMissingDisplayValue(value)) {
            return this.formatDisplayValue(value);
        }

        return (
            <span
                className="missing-info-indicator missing-info-indicator--icon-only"
                title={this.noInfoLabel}
                aria-label={this.noInfoLabel}
            >
                <AlertIcon className="missing-info-icon" aria-hidden="true" focusable="false" />
            </span>
        );
    }

    getErrorStatus(error: unknown): number | undefined {
        return (error as { response?: { status?: number } })?.response?.status;
    }

    handleUnauthorized(error: unknown): boolean {
        if (this.getErrorStatus(error) !== 401) {
            return false;
        }

        if (this.unauthorizedHandled) {
            return true;
        }

        this.unauthorizedHandled = true;
        AuthenticationService.clearSession();
        AlertService.warn(t("Sua sessão expirou. Faça login novamente."));
        globalThis.location.href = LOGIN_ROUTE;
        return true;
    }

    isStaleRequest(requestId: number): boolean {
        return requestId !== this.latestRequestId;
    }

    state: InterviewReportState = {
        loading: true,
        exportLoading: false,
        currentPage: 1,
        itemsPerPage: 10,
        filters: {
            month: [],
            week: "",
            recruiter: [],
            evaluator: [],
            company: [],
        },
        months: [],
        recruiters: [],
        evaluators: [],
        companies: [],
        dashboardRows: [],
        detailsRows: [],
        dataAnimationKey: 0,
        interviewType: {
            client: 0,
            proactive: 0,
        },
        totalProcesses: 0,
    };

    componentDidMount() {
        this.loadInitialData();
    }

    handlePageChange = (page: number) => {  
        const totalPages = Math.max(1, Math.ceil(this.state.detailsRows.length / this.state.itemsPerPage));
        const nextPage = Math.min(Math.max(page, 1), totalPages);
        this.setState({ currentPage: nextPage });
    };

    normalizeOptions(data: unknown): OptionLike[] {
        if (Array.isArray(data)) {
            return data as OptionLike[];
        }

        const dataRecord = data as { data?: unknown; items?: unknown } | undefined;

        const nestedData = dataRecord?.data;
        if (Array.isArray(nestedData)) {
            return nestedData as OptionLike[];
        }

        const nestedItems = dataRecord?.items;
        if (Array.isArray(nestedItems)) {
            return nestedItems as OptionLike[];
        }

        return [];
    }

    pickFirstArray<T>(...candidates: unknown[]): T[] {
        for (const candidate of candidates) {
            if (Array.isArray(candidate)) {
                return candidate as T[];
            }
        }

        return [];
    }

    getOptionLabel(option: OptionLike, fallbackLabel: string): string {
        if (typeof option === "string" || typeof option === "number") {
            return String(option);
        }

        const optionRecord = option as Record<string, unknown>;
        const label =
            optionRecord.label ||
            optionRecord.displayName ||
            optionRecord.name ||
            optionRecord.title ||
            optionRecord.value ||
            fallbackLabel;

        return this.toSafeText(label) || fallbackLabel;
    }

    getOptionValue(option: OptionLike): string {
        if (typeof option === "string" || typeof option === "number") {
            return String(option);
        }

        const optionRecord = option as Record<string, unknown>;
        const value = optionRecord._id || optionRecord.id || optionRecord.value || optionRecord.name || "";
        return this.toSafeText(value);
    }


    buildRollingMonthsFromBaseline(maxMonths = 12, baselineMonthKey = "2026-06"): OptionLike[] {
        const normalizedBaseline = this.normalizeMonthKey(baselineMonthKey);
        const baselineParts = normalizedBaseline.split("-");

        if (baselineParts.length !== 2) {
            return [];
        }

        const baselineYear = Number(baselineParts[0]);
        const baselineMonth = Number(baselineParts[1]);

        if (Number.isNaN(baselineYear) || Number.isNaN(baselineMonth) || baselineMonth < 1 || baselineMonth > 12) {
            return [];
        }

        const baselineDate = new Date(baselineYear, baselineMonth - 1, 1);
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), 1);

        if (baselineDate > currentDate) {
            return [];
        }

        const monthKeys: string[] = [];
        const cursor = new Date(baselineDate);

        while (cursor <= currentDate) {
            const month = String(cursor.getMonth() + 1).padStart(2, "0");
            monthKeys.push(`${cursor.getFullYear()}-${month}`);
            cursor.setMonth(cursor.getMonth() + 1);
        }

        const rollingMonthKeys = monthKeys.slice(-maxMonths);

        return rollingMonthKeys.map((monthKey: string) => ({
            value: monthKey,
            label: monthKey,
        }));
    }

    normalizeMonthKey(rawValue: string): string {
        const value = String(rawValue || "").trim();
        const monthPattern = /^(\d{4})[-/](\d{1,2})$/;
        const match = monthPattern.exec(value);

        if (!match) {
            return "";
        }

        const year = match[1];
        const month = String(Number(match[2])).padStart(2, "0");
        return `${year}-${month}`;
    }

    formatMonthLabel(monthValue: string): string {
        const monthKey = this.normalizeMonthKey(monthValue);
        if (!monthKey) {
            return monthValue;
        }

        const [year, month] = monthKey.split("-");
        const monthDate = new Date(Number(year), Number(month) - 1, 1);
        const monthName = monthDate.toLocaleDateString("pt-BR", { month: "long" });
        const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        return `${capitalizedMonth}/${year}`;
    }

    getCurrentMonthKey(): string {
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        return `${now.getFullYear()}-${month}`;
    }


    getForwardMonths(months: OptionLike[]): OptionLike[] {
        const rollingMonths = this.buildRollingMonthsFromBaseline(12, "2026-06");
        const allowedMonths = new Set(
            rollingMonths
                .map((entry: OptionLike) => this.normalizeMonthKey(this.getOptionValue(entry)))
                .filter(Boolean)
        );
        const uniqueMonths = new Map<string, OptionLike>();

        const saveMonth = (entry: OptionLike) => {
            const monthKey = this.normalizeMonthKey(this.getOptionValue(entry));
            if (!monthKey || !allowedMonths.has(monthKey)) {
                return;
            }

            if (!uniqueMonths.has(monthKey)) {
                uniqueMonths.set(monthKey, entry);
            }
        };

        months.forEach(saveMonth);
        rollingMonths.forEach(saveMonth);

        return Array.from(uniqueMonths.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map((entry) => entry[1]);
    }

    buildCompaniesFromDetails(rows: GenericRow[]): SelectOption[] {
        const uniqueCompanies = new Map<string, SelectOption>();

        rows.forEach((row: GenericRow) => {
            const companyValue = this.getCompanyValue(row, "").trim();
            if (!companyValue || companyValue === "-") {
                return;
            }

            if (!uniqueCompanies.has(companyValue)) {
                uniqueCompanies.set(companyValue, {
                    value: companyValue,
                    label: companyValue,
                });
            }
        });

        return Array.from(uniqueCompanies.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    }

    buildEvaluatorsFromDetails(rows: GenericRow[]): SelectOption[] {
        const uniqueEvaluators = new Map<string, SelectOption>();

        rows.forEach((row: GenericRow) => {
            const evaluatorValue = String(
                this.getDetailsValue(row, ["Avaliador", "evaluatorName", "evaluator"], ["avaliador"], "")
            ).trim();

            if (!evaluatorValue || evaluatorValue === "-") {
                return;
            }

            if (!uniqueEvaluators.has(evaluatorValue)) {
                uniqueEvaluators.set(evaluatorValue, {
                    value: evaluatorValue,
                    label: evaluatorValue,
                });
            }
        });

        return Array.from(uniqueEvaluators.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    }

    buildRecruitersFromDetails(rows: GenericRow[]): SelectOption[] {
        const uniqueRecruiters = new Map<string, SelectOption>();

        rows.forEach((row: GenericRow) => {
            const recruiterValue = String(
                this.getDetailsValue(row, ["Recrutador Responsável da Vaga", "recruiterName", "recruiter"], ["recrutador", "vaga"], "")
            ).trim();

            if (!recruiterValue || recruiterValue === "-") {
                return;
            }

            if (!uniqueRecruiters.has(recruiterValue)) {
                uniqueRecruiters.set(recruiterValue, {
                    value: recruiterValue,
                    label: recruiterValue,
                });
            }
        });

        return Array.from(uniqueRecruiters.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    }

    getBackendFilters(month: string): InterviewAnalyticsFilters {
        return {
            month,
            week: this.state.filters.week,
        };
    }

    normalizeDetailsResponse(interviewsResponse: unknown): GenericRow[] {
        const responseData = (interviewsResponse as { data?: unknown })?.data;

        return this.pickFirstArray<GenericRow>(
            responseData,
            (responseData as Record<string, unknown>)?.data,
            (responseData as Record<string, unknown>)?.items,
            (responseData as Record<string, unknown>)?.rows,
            (responseData as Record<string, unknown>)?.interviews,
            (responseData as Record<string, unknown>)?.result,
            ((responseData as Record<string, unknown>)?.data as Record<string, unknown>)?.rows,
            ((responseData as Record<string, unknown>)?.data as Record<string, unknown>)?.interviews
        );
    }

    getWeekOfMonth(year: number, month: number, day: number): string {
        if (
            !Number.isInteger(year) ||
            !Number.isInteger(month) ||
            !Number.isInteger(day) ||
            month < 1 ||
            month > 12 ||
            day < 1 ||
            day > 31
        ) {
            return "";
        }

        // Weeks always start on a Monday and end on the following Sunday, so a
        // weekend closes the week it belongs to. When the month starts on a
        // Saturday or Sunday those days join week 1 instead of forming a week of
        // their own, which keeps the month within five weeks.
        const firstOfMonth = new Date(year, month - 1, 1);
        const firstWeekdayOffset = (firstOfMonth.getDay() + 6) % 7;
        const calendarWeek = Math.ceil((day + firstWeekdayOffset) / 7);
        const startsOnWeekend = firstWeekdayOffset >= 5;

        return String(Math.max(1, startsOnWeekend ? calendarWeek - 1 : calendarWeek));
    }

    getWeekFromDelimitedDate(value: string, delimiter: "/" | "-"): string {
        const parts = delimiter === "-"
            ? value.split("T")[0].split("-")
            : value.split("/");

        if (parts.length !== 3) {
            return "";
        }

        const [year, month, day] = delimiter === "-"
            ? [Number(parts[0]), Number(parts[1]), Number(parts[2])]
            : [Number(parts[2]), Number(parts[1]), Number(parts[0])];

        return this.getWeekOfMonth(year, month, day);
    }

    getWeekFromDateString(dateValue: unknown): string {
        if (!dateValue || typeof dateValue !== "string") {
            return "";
        }

        const trimmed = dateValue.trim();
        const slashWeek = trimmed.includes("/") ? this.getWeekFromDelimitedDate(trimmed, "/") : "";

        if (slashWeek) {
            return slashWeek;
        }

        const dashWeek = trimmed.includes("-") ? this.getWeekFromDelimitedDate(trimmed, "-") : "";

        if (dashWeek) {
            return dashWeek;
        }

        const parsedDate = new Date(trimmed);
        if (!Number.isNaN(parsedDate.getTime())) {
            return this.getWeekOfMonth(
                parsedDate.getFullYear(),
                parsedDate.getMonth() + 1,
                parsedDate.getDate()
            );
        }

        return "";
    }

    toSafeText(value: unknown): string {
        if (value === null || value === undefined) {
            return "";
        }

        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
            return String(value);
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? "" : value.toISOString();
        }

        if (Array.isArray(value)) {
            return value.map((item: unknown) => this.toSafeText(item)).filter(Boolean).join(", ");
        }

        if (typeof value === "object") {
            const valueRecord = value as Record<string, unknown>;
            const preferredText =
                valueRecord.displayName ??
                valueRecord.label ??
                valueRecord.name ??
                valueRecord.title ??
                valueRecord.value ??
                valueRecord.id ??
                valueRecord._id;

            if (preferredText === null || preferredText === undefined || typeof preferredText === "object") {
                return "";
            }

            if (
                typeof preferredText === "string" ||
                typeof preferredText === "number" ||
                typeof preferredText === "boolean" ||
                typeof preferredText === "bigint"
            ) {
                return String(preferredText);
            }

            return "";
        }

        return "";
    }

    // Recruiter/evaluator/company filters store the UNCHECKED (removed) names,
    // so a row matches only when its value is not among the excluded ones.
    matchesNotExcludedValues(value: unknown, excludedValues: string[]): boolean {
        if (excludedValues.length === 0) {
            return true;
        }

        const normalizedValue = this.normalizeLooseKey(this.toSafeText(value));
        return !excludedValues.some((excludedValue) => (
            normalizedValue === this.normalizeLooseKey(excludedValue)
        ));
    }

    // Exclusions kept from a previous period may point to names that no longer
    // exist in the current data; dropping them keeps the filter in sync with
    // the options actually offered in the dropdown.
    keepExistingExclusions(excludedValues: string[], options: SelectOption[]): string[] {
        const availableValues = new Set(options.map((option) => option.value));
        return excludedValues.filter((value) => availableValues.has(value));
    }

    applyLocalFilters(rows: GenericRow[], filters: Pick<InterviewReportFilters, "recruiter" | "evaluator" | "company">): GenericRow[] {
        const { recruiter, evaluator, company } = filters;
        return rows.filter((row: GenericRow) => {
            const details = this.extractDetailsRow(row);

            return (
                this.matchesNotExcludedValues(details.recruiter, recruiter) &&
                this.matchesNotExcludedValues(details.evaluator, evaluator) &&
                this.matchesNotExcludedValues(details.company, company)
            );
        });
    }

    buildDashboardRowsFromDetails(rows: GenericRow[]): DashboardRow[] {
        const grouped = new Map<string, DashboardAggregateRow>();

        rows.forEach((row: GenericRow) => {
            const evaluatorName = String(
                this.getDetailsValue(row, ["Avaliador", "evaluatorName", "evaluator"], ["avaliador"], "-")
            ).trim() || "-";
            const interviewDate = String(this.getDetailsValue(row, ["Data Da Entrevista", "interviewDate", "dataEntrevista"], ["data", "entrevista"], ""));
            const registerDate = String(this.getDetailsValue(row, ["Data Do Registro", "createdAt", "registerDate", "dataRegistro"], ["data", "registro"], ""));
            const weekSource = interviewDate || registerDate;
            const week = Number(this.getWeekFromDateString(weekSource));

            const safeWeek = Number.isNaN(week) || week < 1 ? 1 : Math.min(week, 5);

            if (!grouped.has(evaluatorName)) {
                grouped.set(evaluatorName, {
                    evaluatorName,
                    week1: 0,
                    week2: 0,
                    week3: 0,
                    week4: 0,
                    week5: 0,
                    totalMonth: 0,
                });
            }

            const bucket = grouped.get(evaluatorName);
            if (!bucket) {
                return;
            }

            const weekKey = `week${safeWeek}` as keyof Pick<DashboardAggregateRow, "week1" | "week2" | "week3" | "week4" | "week5">;
            bucket[weekKey] += 1;
            bucket.totalMonth += 1;
        });

        return Array.from(grouped.values())
            .sort((a: DashboardAggregateRow, b: DashboardAggregateRow) => String(a.evaluatorName || "").localeCompare(String(b.evaluatorName || ""), "pt-BR"));
    }

    buildInterviewTypeFromDetails(rows: GenericRow[]): InterviewTypeSummary {
        return buildInterviewTypeSummary(
            rows.map((row: GenericRow) => this.getCompanyValue(row, ""))
        );
    }

    normalizeLooseKey(value: string): string {
        return String(value || "")
            .normalize("NFD")
            .replaceAll(/[\u0300-\u036f]/g, "")
            .replaceAll(/[^a-zA-Z0-9]/g, "")
            .toLowerCase();
    }

    getValueByPossibleKeys(row: GenericRow, keys: string[]): unknown {
        for (const key of keys) {
            if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
                return row[key];
            }
        }
        return undefined;
    }

    getValueByKeyTokens(row: GenericRow, tokens: string[]): unknown {
        const normalizedTokens = tokens.map((token) => this.normalizeLooseKey(token));
        const rowKeys = Object.keys(row || {});

        const matchedKey = rowKeys.find((key) => {
            const normalizedKey = this.normalizeLooseKey(key);
            return normalizedTokens.every((token) => normalizedKey.includes(token));
        });

        if (!matchedKey) {
            return undefined;
        }

        const value = row?.[matchedKey];
        return value !== undefined && value !== null && value !== "" ? value : undefined;
    }

    getDetailsValue(row: GenericRow, directKeys: string[], tokenKeys: string[], fallback = "-"): unknown {
        const directValue = this.getValueByPossibleKeys(row, directKeys);
        if (directValue !== undefined) {
            return directValue;
        }

        const tokenValue = this.getValueByKeyTokens(row, tokenKeys);
        if (tokenValue !== undefined) {
            return tokenValue;
        }
        return fallback;
    }

    getCompanyValue(row: GenericRow, fallback = "-"): string {
        return String(this.getDetailsValue(row, this.companyDirectKeys, this.companyTokenKeys, fallback));
    }

    extractDetailsRow(row: GenericRow): DetailsExtractedRow {
        const extractedRow: DetailsExtractedRow = {
            company: this.getCompanyValue(row),
            vacancy: this.getDetailsValue(row, ["Vaga", "jobTitle", "vacancy", "vaga"], ["vaga"]),
            candidate: this.getDetailsValue(row, ["Candidato", "candidateName", "candidate"], ["candidato"]),
            linkedin: this.getDetailsValue(row, ["LinkedIn", "linkedin"], ["linkedin"]),
            recruiter: this.getDetailsValue(row, ["Recrutador Responsável da Vaga", "recruiterName", "recruiter"], ["recrutador", "vaga"]),
            stage: this.getDetailsValue(row, ["Etapa", "stage", "etapa"], ["etapa"]),
            interviewDate: this.getDetailsValue(row, ["Data Da Entrevista", "interviewDate", "dataEntrevista"], ["data", "entrevista"]),
            evaluator: this.getDetailsValue(row, ["Avaliador", "evaluatorName", "evaluator"], ["avaliador"]),
            registerDate: this.getDetailsValue(row, ["Data Do Registro", "createdAt", "registerDate", "dataRegistro"], ["data", "registro"]),
        };

        return extractedRow;
    }

    getLinkedInHref(value: unknown): string {
        const normalized = normalizeLinkedIn(this.toSafeText(value));
        if (!normalized) {
            return "";
        }

        return /^https?:\/\//i.test(normalized)
            ? normalized
            : `https://${normalized}`;
    }

    renderLinkedInAction(value: unknown) {
        const href = this.getLinkedInHref(value);
        const isValid = isValidLinkedIn(href);

        if (!isValid) {
            return (
                <span
                    className="linkedin-missing-indicator"
                    title={t("LinkedIn não informado")}
                    aria-label={t("LinkedIn não informado")}
                >
                    <AlertIcon className="linkedin-missing-icon" aria-hidden="true" focusable="false" />
                </span>
            );
        }

        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
                title={t("Perfil LinkedIn")}
                aria-label={t("Perfil LinkedIn")}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                        fill="currentColor"
                        d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.05-1.86-3.05-1.86 0-2.14 1.45-2.14 2.95v5.67H9.33V9h3.42v1.56h.05c.48-.9 1.64-1.86 3.38-1.86 3.62 0 4.28 2.38 4.28 5.48v6.27zM5.34 7.43a2.07 2.07 0 11.01-4.14 2.07 2.07 0 01-.01 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"
                    />
                </svg>
            </a>
        );
    }

    escapeCsvValue(value: unknown): string {
        const normalized = this.toSafeText(value).replace(/\r?\n|\r/g, " ").trim();
        const escaped = normalized.replaceAll('"', '""');
        return `"${escaped}"`;
  
    }

    buildCsvLines(rows: GenericRow[]): string[] {
        const headers = [
            "EMPRESA",
            "VAGA",
            "CANDIDATO",
            "LINKEDIN",
            "RESPONSÁVEL PELA VAGA",
            "ETAPA",
            "ENTREVISTA",
            "AVALIADOR",
            "REGISTRO",
        ];
        const csvLines = [
            headers.map((header) => this.escapeCsvValue(header)).join(","),
        ];

        rows.forEach((row: GenericRow) => {
            const details = this.extractDetailsRow(row);

            const values = [
                details.company,
                details.vacancy,
                details.candidate,
                this.getLinkedInHref(details.linkedin) || "-",
                details.recruiter,
                details.stage,
                details.interviewDate,
                details.evaluator,
                details.registerDate
            ];

            csvLines.push(values.map((value) => this.escapeCsvValue(value)).join(","));
        });

        return csvLines;
    }

    downloadCsv(lines: string[], fileName: string) {
        if (!lines || lines.length <= 1) {
            return;
        }

        const csvContent = `\uFEFF${lines.join("\n")}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
    }

    exportDetailsToCsv = () => {
        if (this.state.exportLoading) {
            return;
        }

        this.setState({ exportLoading: true });

        try {
            const { detailsRows } = this.state;

            if (!detailsRows || detailsRows.length === 0) {
                return;
            }

            const csvLines = this.buildCsvLines(detailsRows);
            const date = new Date().toISOString().slice(0, 10);
            this.downloadCsv(csvLines, `relatorio-entrevistas-${date}.csv`);
        } catch (error) {
            if (!this.handleUnauthorized(error)) {
                AlertService.error(t("Não foi possível exportar o CSV."));
            }
        } finally {
            this.setState({ exportLoading: false });
        }
    };

    async loadInitialData() {
        this.setState({ loading: true });
        try {
            const monthsResponse = await InterviewService.listMonths();

            const normalizedMonths = this.getForwardMonths(this.normalizeOptions(monthsResponse.data));
            const currentMonthKey = this.getCurrentMonthKey();
            const hasCurrentMonth = normalizedMonths.some(
                (month: OptionLike) => this.normalizeMonthKey(this.getOptionValue(month)) === currentMonthKey
            );
            const fallbackMonth = this.getOptionValue(normalizedMonths.at(-1) ?? "");
            const defaultMonth = hasCurrentMonth ? currentMonthKey : fallbackMonth;

            this.setState(
                (prevState: InterviewReportState) => ({
                    companies: [],
                    recruiters: [],
                    evaluators: [],
                    months: normalizedMonths,
                    filters: {
                        ...prevState.filters,
                        month: defaultMonth ? [defaultMonth] : [],
                    },
                }),
                () => {
                    this.loadReportData();
                }
            );
        } catch (error) {

            if (this.handleUnauthorized(error)) {
                return;
            }

            AlertService.error(t("Não foi possível carregar os filtros do relatório."));
            this.setState({ loading: false });
        }
    }

    async loadReportData() {
        const requestId = ++this.latestRequestId;
        this.setState({ loading: true });

        try {
            const { filters } = this.state;
            const selectedMonths = filters.month.length > 0
                ? filters.month
                : this.state.months
                    .map((month) => this.getOptionValue(month))
                    .filter(Boolean);
            const interviewsResponses = await Promise.all(
                selectedMonths.map((month) => (
                    InterviewService.getInterviewAnalytics(this.getBackendFilters(month))
                ))
            );

            if (this.isStaleRequest(requestId)) {
                return;
            }

            const normalizedDetails = interviewsResponses.flatMap((response) => (
                this.normalizeDetailsResponse(response)
            ));
            const availableRecruiters = this.buildRecruitersFromDetails(normalizedDetails);
            const availableCompanies = this.buildCompaniesFromDetails(normalizedDetails);
            const availableEvaluators = this.buildEvaluatorsFromDetails(normalizedDetails);
            const activeFilters = {
                recruiter: this.keepExistingExclusions(filters.recruiter, availableRecruiters),
                evaluator: this.keepExistingExclusions(filters.evaluator, availableEvaluators),
                company: this.keepExistingExclusions(filters.company, availableCompanies),
            };
            const filteredDetails = this.applyLocalFilters(normalizedDetails, activeFilters);
            const localDashboardRows = this.buildDashboardRowsFromDetails(filteredDetails);
            const localInterviewType = this.buildInterviewTypeFromDetails(filteredDetails);
            const interviewTypeClient = localInterviewType.client;
            const interviewTypeProactive = localInterviewType.proactive;

            if (this.isStaleRequest(requestId)) {
                return;
            }

            this.setState((prevState: InterviewReportState) => ({
                dashboardRows: localDashboardRows,
                detailsRows: filteredDetails,
                recruiters: availableRecruiters,
                companies: availableCompanies,
                evaluators: availableEvaluators,
                filters: {
                    ...prevState.filters,
                    ...activeFilters,
                },
                interviewType: {
                    client: interviewTypeClient,
                    proactive: interviewTypeProactive,
                },
                totalProcesses: filteredDetails.length,
                currentPage: 1,
                loading: false,
                dataAnimationKey: prevState.dataAnimationKey + 1,
            }));
        } catch (error) {

            if (this.handleUnauthorized(error)) {
                return;
            }

            AlertService.error(t("Não foi possível carregar os dados do relatório."));

            if (requestId === this.latestRequestId) {
                this.setState({
                    loading: false,
                });
            }
        }
    }
    handleFilterChange = (name: InterviewFilterName, values: string[]) => {
        this.setState(
            (prevState: InterviewReportState) => ({
                filters: {
                    ...prevState.filters,
                    [name]: values,
                },
                currentPage: 1,
            }),
            () => {
                this.loadReportData();
            }
        );
    };

    handleMonthFilterChange = (values: string[]) => this.handleFilterChange("month", values);
    handleRecruiterFilterChange = (values: string[]) => this.handleFilterChange("recruiter", values);
    handleEvaluatorFilterChange = (values: string[]) => this.handleFilterChange("evaluator", values);
    handleCompanyFilterChange = (values: string[]) => this.handleFilterChange("company", values);

    getRowAnimationStyle(index: number) {
        const cappedIndex = Math.min(index, 11);
        return {
            animationDelay: `${80 + cappedIndex * 55}ms`,
        };
    }

    render() {
        const {
            loading,
            exportLoading,
            months,
            recruiters,
            evaluators,
            companies,
            dashboardRows,
            detailsRows,
            currentPage,
            itemsPerPage,
            interviewType,
            totalProcesses,
            filters,
            dataAnimationKey,
        } = this.state;

        const totalPages = Math.max(1, Math.ceil(detailsRows.length / itemsPerPage));
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedDetailsRows = detailsRows.slice(startIndex, endIndex);
        const weeklyTotals = this.getWeeklyTotals(dashboardRows);
        const monthlyTotal = weeklyTotals.reduce((sum, { total }) => sum + total, 0);
        const monthOptions = months.map((month: OptionLike, index: number) => ({
            value: this.getOptionValue(month),
            label: this.formatMonthLabel(
                this.getOptionValue(month) ||
                this.getOptionLabel(month, `${t("MÊS")} ${index + 1}`)
            ),
        }));

        return (
            <>
                <Loading show={loading}></Loading>
                <div className={`interview-report-container mt-4 ${loading ? "report-loading" : "report-loaded"}`}>
                    <div className="interview-report-header">
                        <div className="interview-report-headline">
                            <div className="interview-report-headline-left">
                                <h1 className="interview-report-title">
                                    {t("Visão Geral De Entrevistas")}
                                </h1>
                            </div>
                        </div>
                        <form className="interview-report-form">
                            <div className="interview-report-form-group">
                                <label htmlFor="week">
                                    {t("MÊS DE REFERÊNCIA")}
                                </label>
                                <div className="filter-control filter-control--multi">
                                    <InterviewMultiSelect
                                        id="week"
                                        options={monthOptions}
                                        selectedValues={filters.month}
                                        allLabel={t("Todos os meses")}
                                        onChange={this.handleMonthFilterChange}
                                    />
                                </div>
                            </div>
                            <div className="interview-report-form-group">
                                <label htmlFor="recruiter">
                                    {t("RESPONSÁVEL PELA VAGA")}
                                </label>
                                <div className="filter-control filter-control--multi">
                                    <InterviewMultiSelect
                                        id="recruiter"
                                        options={recruiters}
                                        selectedValues={filters.recruiter}
                                        allLabel={t("Todos os Recrutadores")}
                                        mode="exclude"
                                        onChange={this.handleRecruiterFilterChange}
                                    />
                                </div>
                            </div>
                            <div className="interview-report-form-group">
                                <label htmlFor="evaluator">
                                    {t("AVALIADOR")}
                                </label>
                                <div className="filter-control filter-control--multi">
                                    <InterviewMultiSelect
                                        id="evaluator"
                                        options={evaluators}
                                        selectedValues={filters.evaluator}
                                        allLabel={t("Todos os Avaliadores")}
                                        mode="exclude"
                                        onChange={this.handleEvaluatorFilterChange}
                                    />
                                </div>
                            </div>

                            <div className="interview-report-form-group">
                                <label htmlFor="company">
                                    {t("EMPRESA")}
                                </label>

                                <div className="filter-control filter-control--multi">
                                    <InterviewMultiSelect
                                        id="company"
                                        options={companies}
                                        selectedValues={filters.company}
                                        allLabel={t("Todas as Empresas")}
                                        mode="exclude"
                                        onChange={this.handleCompanyFilterChange}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="interview-report-table-dashboard">
                    <div className="interview-report-dashboard-left">
                        <div className="dashboard-table-card">
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th>{t("AVALIADOR")}</th>
                                        <th>{t("SEMANA 1")}</th>
                                        <th>{t("SEMANA 2")}</th>
                                        <th>{t("SEMANA 3")}</th>
                                        <th>{t("SEMANA 4")}</th>
                                        <th>{t("SEMANA 5")}</th>
                                        <th>{t("TOTAL MÊS")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dashboardRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="interview-report-empty-cell">
                                                <div className="interview-report-empty-state">
                                                    <strong>{t("Nenhum dado encontrado")}</strong>
                                                    <span>{t("Ainda não há registros para o período selecionado.")}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        dashboardRows.map((row: any, index: number) => {
                                            return (
                                                <tr
                                                    key={`${row?._id || index}-${dataAnimationKey}`}
                                                    className="stagger-row"
                                                    style={this.getRowAnimationStyle(index)}
                                                >
                                                    <td>{this.formatDisplayValue(row?.evaluatorName)}</td>
                                                    <td>{this.formatDisplayValue(row?.week1)}</td>
                                                    <td>{this.formatDisplayValue(row?.week2)}</td>
                                                    <td>{this.formatDisplayValue(row?.week3)}</td>
                                                    <td>{this.formatDisplayValue(row?.week4)}</td>
                                                    <td>{this.formatDisplayValue(row?.week5)}</td>
                                                    <td>{this.formatDisplayValue(row?.totalMonth)}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                                {dashboardRows.length > 0 && (
                                    <tfoot>
                                        <tr className="interview-report-totals-row">
                                            <td><strong>{t("Total de avaliações por semana")}</strong></td>
                                            {weeklyTotals.map(({ key, total }) => (
                                                <td key={`total-${key}`}><strong>{total}</strong></td>
                                            ))}
                                            <td><strong>{monthlyTotal}</strong></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </Table>
                        </div>
                    </div>
                    <div className="interview-report-dashboard-right">
                        <div className="dashboard-card">
                            <div className="dashboard-card-header">
                                <h6>{t("TIPOS DE ENTREVISTA")}</h6>
                                <ClipboardIcon className="dashboard-icon" />
                            </div>
                            <div className="dashboard-card-body">
                                <div className="dashboard-value">
                                    <strong>{interviewType.client}</strong>
                                    <span>{t("CLIENTE")}</span>
                                </div>
                                <div className="dashboard-divider"></div>
                                <div className="dashboard-value">
                                    <strong>{interviewType.proactive}</strong>
                                    <span>{t("PROATIVA")}</span>
                                </div>
                            </div>
                        </div>
                        <div className="dashboard-card">
                            <div className="dashboard-card-header">
                                <h6>{t("TOTAL DE PROCESSOS")}</h6>
                                <TrendingIcon className="dashboard-icon dashboard-icon--success" />
                            </div>
                            <div className="dashboard-total">
                                <strong>{totalProcesses}</strong>
                                <span>{t("REGISTROS ATIVOS")}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="analytics-card">
                    <div className="analytics-header">
                        <div>
                            <h2>
                                {t("Registros Analíticos de Entrevistas")}
                            </h2>
                        </div>
                        <div className="analytics-header-actions">
                            <div className="analytics-total">
                                ETAPA:
                                {" "}
                                <span>
                                    {detailsRows.length > 0
                                        ? this.formatDisplayValue(this.getDetailsValue(detailsRows[0], ["Etapa", "stage", "etapa"], ["etapa"], "-"))
                                        : "-"}
                                </span>
                            </div>
                            <div className="analytics-total">
                                TOTAL:
                                {" "}
                                <span>
                                    {detailsRows.length} registros
                                </span>
                            </div>
                            <button
                                type="button"
                                className="analytics-export-btn"
                                onClick={this.exportDetailsToCsv}
                                disabled={exportLoading}
                            >
                                <span className="analytics-export-btn-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 24 24" focusable="false">
                                        <path
                                            fill="currentColor"
                                            d="M12 3a1 1 0 0 1 1 1v8.59l2.3-2.3a1 1 0 1 1 1.4 1.42l-4 3.99a1 1 0 0 1-1.4 0l-4-3.99a1 1 0 1 1 1.4-1.42l2.3 2.3V4a1 1 0 0 1 1-1zm-7 14a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1z"
                                        />
                                    </svg>
                                </span>
                                {" "}
                                <span>{exportLoading ? t("Exportando...") : t("Exportar CSV")}</span>
                            </button>
                        </div>
                    </div>
                    <div className="interview-report-table-details">
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th>{t("EMPRESA")}</th>
                                    <th>{t("VAGA")}</th>
                                    <th>{t("CANDIDATO")}</th>
                                    <th>{t("LINKEDIN")}</th>
                                    <th>{t("RESPONSÁVEL")}</th>
                                    <th>{t("ENTREVISTA")}</th>
                                    <th>{t("AVALIADOR")}</th>
                                    <th>{t("REGISTRO")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedDetailsRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="interview-report-empty-cell">
                                            <div className="interview-report-empty-state">
                                                <strong>{t("Nenhum registro encontrado")}</strong>
                                                <span>{t("Ajuste os filtros para visualizar entrevistas nesta tabela.")}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedDetailsRows.map((row: any, index: number) => {

                                        const details = this.extractDetailsRow(row);

                                        return (
                                            <tr
                                                key={`${startIndex + index}-${dataAnimationKey}`}
                                                className="stagger-row"
                                                style={this.getRowAnimationStyle(index)}
                                            >
                                                <td>{this.formatDisplayValue(details.company)}</td>
                                                <td>{this.formatDisplayValue(details.vacancy)}</td>
                                                <td>{this.formatDisplayValue(details.candidate)}</td>
                                                <td className="linkedin-cell">{this.renderLinkedInAction(details.linkedin)}</td>
                                                <td>{this.formatDisplayValue(details.recruiter)}</td>
                                                <td>{this.formatDisplayValue(details.interviewDate)}</td>
                                                <td>{this.formatDisplayValue(details.evaluator)}</td>
                                                <td>{this.renderMissingInfoWithAlert(details.registerDate)}</td>
                                            </tr>
                                        );
                                    })
                                )}

                            </tbody>
                        </Table>
                    </div>
                    {detailsRows.length > 0 && (
                        <div className="interview-report-table-pagination">
                            <button
                                type="button"
                                className="button-pagination"
                                disabled={currentPage === 1}
                                onClick={() => this.handlePageChange(currentPage - 1)}
                            >
                                {t("Anterior")}
                            </button>
                            <div className="interview-report-pagination">
                                <span>
                                    {t("Página")} {currentPage} {t("de")} {totalPages}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="button-pagination"
                                disabled={currentPage === totalPages}
                                onClick={() => this.handlePageChange(currentPage + 1)}
                            >
                                {t("Próxima")}
                            </button>
                        </div>
                    )}
                </div>
            </>
        );
    }
}

export default InterviewReport; 